import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Search, History, FileText,
  Key, CreditCard, Settings, LogOut, Shield, Menu, X,
} from 'lucide-react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';

const NAV = [
  { label: 'Overview',      icon: LayoutDashboard, href: '/dashboard'          },
  { label: 'Wallet Scanner',icon: Search,           href: '/dashboard/scanner'  },
  { label: 'Scan History',  icon: History,          href: '/dashboard/history'  },
  { label: 'Saved Reports', icon: FileText,         href: '/dashboard/reports'  },
  { label: 'API Access',    icon: Key,              href: '/dashboard/api'      },
  { label: 'Billing',       icon: CreditCard,       href: '/dashboard/billing'  },
  { label: 'Settings',      icon: Settings,         href: '/dashboard/settings' },
];

export default function DashboardLayout() {
  const [open, setOpen]   = useState(false);
  const { user, logout }  = useAuth();
  const location          = useLocation();
  const navigate          = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (href) =>
    href === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname.startsWith(href);

  const initial = (user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <div className="min-h-screen bg-[#080c16] flex">

      {/* ── Mobile overlay ──────────────────────────────────────────── */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 w-60 flex flex-col',
          'bg-[#0a0e1a] border-r border-white/[0.06]',
          'transition-transform duration-300 ease-in-out',
          'lg:relative lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-white/[0.06] flex-shrink-0">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center shadow-md">
              <Shield size={13} className="text-white" />
            </div>
            <span className="text-white font-bold text-sm">
              Zenith<span className="text-[#00E5FF]">Cipher</span>
            </span>
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="lg:hidden p-1 text-slate-500 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map(({ label, icon: Icon, href }) => {
            const active = isActive(href);
            return (
              <Link
                key={href}
                to={href}
                onClick={() => setOpen(false)}
                className={[
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                  active
                    ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/15'
                    : 'text-slate-500 hover:text-slate-200 hover:bg-white/[0.05] border border-transparent',
                ].join(' ')}
              >
                <Icon size={15} className={active ? 'text-[#00E5FF]' : 'text-slate-600'} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="px-3 py-4 border-t border-white/[0.06] flex-shrink-0">
          <div className="flex items-center gap-3 px-3 py-2.5 mb-2 rounded-xl bg-white/[0.03]">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">{user?.name || user?.email?.split('@')[0] || 'User'}</p>
              <p className="text-[11px] text-slate-600 capitalize">{user?.plan || 'Free'} Plan</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-slate-500 hover:text-red-400 hover:bg-red-500/8 transition-all"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <header className="h-16 flex items-center justify-between px-5 border-b border-white/[0.06] bg-[#080c16]/95 backdrop-blur-xl sticky top-0 z-30 flex-shrink-0">
          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 text-slate-500 hover:text-white rounded-lg hover:bg-white/5 transition-all"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>

          {/* Plan badge */}
          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.07] rounded-xl px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse flex-shrink-0" />
              <span className="text-xs text-slate-400 capitalize font-medium">
                {user?.plan || 'Free'} Plan
              </span>
            </div>

            {/* User avatar */}
            <Link
              to="/dashboard/settings"
              className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center text-xs font-bold text-white hover:opacity-80 transition-opacity flex-shrink-0"
              title="Settings"
            >
              {initial}
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
